import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Button,
  OutlinedInput,
  Typography,
} from "@mui/material";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ApiIcon from "@mui/icons-material/Api";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

// API Configuration - OR CUBE External API
const API_BASE = "https://ai.orcube.com/client/v1";
const CLIENT_ID = "c38fd502-daa4-4c46-a385-71f2fc08ab5c";
const CLIENT_SECRET = "7df2243bdbcc5d735675160fad3a1be3fcdface1e98cb3806dcb661aaaa67cca";

// API Helper
const apiRequest = async (endpoint, method = "GET", body = null) => {
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Client-ID": CLIENT_ID,
      "X-Client-Secret": CLIENT_SECRET,
    },
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(`${API_BASE}${endpoint}`, options);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  if (response.status === 204) return null;
  return response.json();
};

// Get category display info
const getCategoryInfo = (category) => {
  switch (category) {
    case "simple_response":
      return {
        label: "Simple Response",
        icon: <SmartToyIcon sx={{ color: "#16a34a", fontSize: 20 }} />,
        bgColor: "#dcfce7",
        textColor: "#166534",
      };
    case "external_api":
      return {
        label: "External API",
        icon: <ApiIcon sx={{ color: "#d97706", fontSize: 20 }} />,
        bgColor: "#fef3c7",
        textColor: "#92400e",
      };
    case "workflow":
      return {
        label: "Workflow",
        icon: <AccountTreeIcon sx={{ color: "#7c3aed", fontSize: 20 }} />,
        bgColor: "#e0e7ff",
        textColor: "#3730a3",
      };
    default:
      return {
        label: category || "Unknown",
        icon: null,
        bgColor: "#f1f5f9",
        textColor: "#475569",
      };
  }
};

const APIIntents = () => {
  const navigate = useNavigate();
  const [intents, setIntents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "success" });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    intent_category: "simple_response",
    examples: [],
    response_config: {},
  });
  const [exampleInput, setExampleInput] = useState("");

  const formShellSx = {
    p: 3,
    borderRadius: 3,
    border: "1px solid #dce3ee",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
  };

  const fieldLabelSx = {
    color: "#344767",
    fontWeight: 700,
    fontSize: "0.9rem",
    mb: 0.65,
    display: "block",
  };

  const vendorInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fff",
      transition: "border-color .2s ease, box-shadow .2s ease",
      padding: "8px 14px !important", // Adjust padding to ensure text visibility
      "& input": {
        width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
      },
      "& fieldset": {
        borderColor: "#0a1935",
      },
      "&:hover fieldset": {
        borderColor: "#0b1a36",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0b3a6e !important",
        borderWidth: "2px !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0b3a6e",
        borderWidth: 2,
      },
    },
  };

  const vendorSelectSx = {
    borderRadius: "10px",
    backgroundColor: "#fff",
    transition: "border-color .2s ease, box-shadow .2s ease",
    padding: "8px 14px !important", // Adjust padding to ensure text visibility
    "& input": {
      width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
    },
    "& .MuiSelect-select": {
      py: "12px",
      fontSize: "14px",
      cursor: "pointer",
    },
    "& fieldset": {
      borderColor: "#112141",
    },
    "&:hover fieldset": {
      borderColor: "#122445",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#0b3a6e !important",
      borderWidth: "2px !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0b3a6e",
      borderWidth: 2,
    },
  };

  // Fetch intents
  const fetchIntents = async () => {
    setIsLoading(true);
    try {
      const data = await apiRequest("/intents");
      setIntents(data.intents || []);
    } catch (error) {
      console.error("Error fetching intents:", error);
      showAlert(`Error: ${error.message}`, "error");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchIntents();
  }, []);

  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "success" }), 5000);
  };

  // Handle form change
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Add example
  const addExample = () => {
    if (exampleInput.trim() && !formData.examples.includes(exampleInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        examples: [...prev.examples, exampleInput.trim()],
      }));
      setExampleInput("");
    }
  };

  // Remove example
  const removeExample = (index) => {
    setFormData((prev) => ({
      ...prev,
      examples: prev.examples.filter((_, i) => i !== index),
    }));
  };

  // Get response config from form
  const getResponseConfig = () => {
    const category = formData.intent_category;
    if (category === "simple_response") {
      return { custom_response: formData.customResponse || "" };
    } else if (category === "external_api") {
      return {
        data_url: formData.apiUrl || "",
        headers: {},
        body: {},
      };
    } else if (category === "workflow") {
      return { workflow_trigger_id: parseInt(formData.workflowId) || null };
    }
    return null;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showAlert("Intent name is required", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        matching_mode: "example",
        examples: formData.examples,
        intent_category: formData.intent_category,
        response_config: getResponseConfig(),
      };

      await apiRequest("/intents", "POST", payload);
      showAlert("Intent created successfully!", "success");

      // Reset form
      setFormData({
        name: "",
        description: "",
        intent_category: "simple_response",
        examples: [],
        response_config: {},
      });

      // Refresh intents
      fetchIntents();
    } catch (error) {
      showAlert(`Error: ${error.message}`, "error");
    }
    setIsSubmitting(false);
  };

  // Delete intent
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this intent?")) return;
    try {
      await apiRequest(`/intents/${id}`, "DELETE");
      showAlert("Intent deleted successfully!", "success");
      fetchIntents();
    } catch (error) {
      showAlert(`Error: ${error.message}`, "error");
    }
  };

  return (
    <DashboardLayout>
      <SoftBox py={3}>
        {/* Page Header */}
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            AI Intents
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Manage your AI chatbot intents and automations
          </SoftTypography>
        </SoftBox>

        {/* Alert */}
        {alert.show && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: alert.type === "success" ? "#dcfce7" : "#fee2e2",
              color: alert.type === "success" ? "#166534" : "#991b1b",
              border: `1px solid ${alert.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            }}
          >
            {alert.message}
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Create Intent Form */}
          <Grid item xs={12}>
            <Card sx={formShellSx}>
              <SoftTypography variant="h5" fontWeight="bold" mb={3}>
                Create Unified Intent
              </SoftTypography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SoftBox mb={1.5}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Intent Name *
                      </SoftTypography>
                      <TextField
                        fullWidth
                        placeholder="e.g. Product FAQs"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        InputProps={{
                          sx: {
                            padding: "8px 14px !important", // Adjust padding to ensure text visibility
                            "& input": {
                              width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
                            },
                            "& fieldset": {
                              borderColor: "#162b52",
                            },
                            "&:hover fieldset": {
                              borderColor: "#0f2142",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#0b3a6e !important",
                              borderWidth: "2px !important",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0b3a6e",
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftBox mb={1.5}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Intent Category *
                      </SoftTypography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={formData.intent_category}
                          onChange={(e) => handleChange("intent_category", e.target.value)}
                          input={<OutlinedInput />}
                          sx={vendorSelectSx}
                          renderValue={() => (
                            <Typography
                              variant="body1"
                              style={{ fontSize: "14px", fontWeight: "normal" }}
                            >
                              Simple Response
                            </Typography>
                          )}
                        >
                          <MenuItem value="simple_response">Simple Response</MenuItem>
                        </Select>
                      </FormControl>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12}>
                    <SoftBox mb={1.5}>
                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Description
                      </SoftTypography>
                      <TextField
                        fullWidth
                        placeholder="Optional description"
                        value={formData.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                        InputProps={{
                          sx: {
                            padding: "8px 14px !important", // Adjust padding to ensure text visibility
                            "& input": {
                              width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
                            },
                            "& fieldset": {
                              borderColor: "#162b52",
                            },
                            "&:hover fieldset": {
                              borderColor: "#0f2142",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#0b3a6e !important",
                              borderWidth: "2px !important",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0b3a6e",
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                    </SoftBox>
                  </Grid>

                  {/* Examples Input */}
                  <Grid item xs={12}>
                    <SoftTypography variant="caption" fontWeight="medium" mb={1}>
                      Matching Phrases (User phrases that trigger this intent)
                    </SoftTypography>
                    <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        placeholder="Type a phrase and press Add"
                        value={exampleInput}
                        onChange={(e) => setExampleInput(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addExample())}
                        InputProps={{
                          sx: {
                            padding: "8px 14px !important", // Adjust padding to ensure text visibility
                            "& input": {
                              width: "calc(100% - 0px) !important", // Adjust input width to prevent text cutoff
                            },
                            "& fieldset": {
                              borderColor: "#162b52",
                            },
                            "&:hover fieldset": {
                              borderColor: "#0f2142",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px rgba(11, 58, 110, 0.16)",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#0b3a6e !important",
                              borderWidth: "2px !important",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0b3a6e",
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                      <IconButton
                        onClick={addExample}
                        sx={{
                          width: 46,
                          height: 46,
                          background: "linear-gradient(135deg, #1f9bf0 0%, #3071af 100%)",
                          color: "#fff",
                          boxShadow: "0 10px 20px rgba(15, 116, 209, 0.28)",
                          transition: "all 0.22s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            background: "linear-gradient(135deg, #1f9bf0 0%, #1f5f9b 100%)",
                          },
                          "&:active": {
                            transform: "scale(0.97)",
                          },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {formData.examples.map((ex, i) => (
                        <Chip
                          key={i}
                          label={ex}
                          onDelete={() => removeExample(i)}
                          sx={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: 500 }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Response Config - Only Simple Response */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        p: 2.2,
                        borderRadius: 3,
                      }}
                    >
                      <SoftTypography variant="caption" fontWeight="bold" mb={2}>
                        Response Configuration
                      </SoftTypography>

                      <SoftTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        sx={fieldLabelSx}
                      >
                        Custom Response
                      </SoftTypography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Enter the response the AI should give..."
                        value={formData.customResponse || ""}
                        onChange={(e) => handleChange("customResponse", e.target.value)}
                        size="small"
                        sx={{
                          ...vendorInputSx,
                          "& .MuiOutlinedInput-root": {
                            ...vendorInputSx["& .MuiOutlinedInput-root"],
                            alignItems: "flex-start",
                            minHeight: 120,
                          },
                          "& .MuiInputBase-inputMultiline": {
                            width: "100% !important",
                            boxSizing: "border-box",
                            overflow: "auto",
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: 2.5,
                        background: "linear-gradient(135deg, #1f9bf0 0%, #3071af 100%)",
                        color: "#fff",
                        boxShadow: "0 14px 26px rgba(15, 116, 209, 0.28)",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-140%",
                          width: "120%",
                          height: "100%",
                          background:
                            "linear-gradient(110deg, transparent 20%, rgba(255,255,255,0.34) 50%, transparent 80%)",
                          transition: "left 0.55s ease",
                        },
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 18px 28px rgba(15, 116, 209, 0.35)",
                        },
                        "&:hover::before": {
                          left: "130%",
                        },
                        "&:active": {
                          transform: "scale(0.98)",
                        },
                        textTransform: "none",
                        px: 3.2,
                        py: 1.05,
                        fontWeight: 600,
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        "+ Create Intent"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Grid>

          {/* Intents List */}
          <Grid item xs={12}>
            <SoftTypography variant="h5" fontWeight="bold" mb={2}>
              Your Intents
            </SoftTypography>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress color="info" />
              </Box>
            ) : intents.filter((i) => i.intent_category === "simple_response").length === 0 ? (
              <Card sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
                <SoftTypography variant="body2" color="text">
                  No simple response intents created yet. Create your first intent!
                </SoftTypography>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {intents
                  .filter((i) => i.intent_category === "simple_response")
                  .map((intent) => {
                    const catInfo = getCategoryInfo(intent.intent_category);
                    return (
                      <Grid item xs={12} sm={6} key={intent.id}>
                        <Card
                          sx={{
                            p: 2.5,
                            borderRadius: 3,
                            borderLeft: `4px solid ${catInfo.textColor}`,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                            "&:hover": { boxShadow: "0 4px 15px rgba(0,0,0,0.1)" },
                            transition: "box-shadow 0.2s",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <SoftTypography variant="h6" fontWeight="bold">
                              {intent.name}
                            </SoftTypography>
                            <Chip
                              icon={catInfo.icon}
                              label={catInfo.label}
                              size="small"
                              sx={{
                                backgroundColor: catInfo.bgColor,
                                color: catInfo.textColor,
                                fontSize: "11px",
                                height: "26px",
                              }}
                            />
                          </Box>

                          <SoftTypography
                            variant="caption"
                            color="text"
                            sx={{ display: "block", mb: 1.5 }}
                          >
                            {intent.description || "No description"}
                          </SoftTypography>

                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                            {(intent.examples || []).slice(0, 3).map((ex, i) => (
                              <Chip
                                key={i}
                                label={ex}
                                size="small"
                                sx={{
                                  backgroundColor: "#f1f5f9",
                                  fontSize: "11px",
                                  height: "22px",
                                }}
                              />
                            ))}
                            {(intent.examples || []).length > 3 && (
                              <Chip
                                label={`+${intent.examples.length - 3} more`}
                                size="small"
                                sx={{
                                  backgroundColor: "#f1f5f9",
                                  fontSize: "11px",
                                  height: "22px",
                                }}
                              />
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              borderTop: "1px solid #f1f5f9",
                              pt: 1.5,
                            }}
                          >
                            <Button
                              size="small"
                              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                              onClick={() => navigate(`/ai-intents/edit/${intent.id}`)}
                              sx={{ textTransform: "none", color: "#64748b", fontSize: "12px" }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                              onClick={() => handleDelete(intent.id)}
                              sx={{ textTransform: "none", color: "#dc2626", fontSize: "12px" }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Chatbot Widget Container Removed - Moved to Global Layout */}
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
};

export default APIIntents;
